import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { PACKAGE_CATEGORIES, BILLING_CYCLES, LOCATION_TYPES } from "../../lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const emptyForm = {
  name: "", category: "Individual", billing_cycle: "Monthly", price: 0, session_count: "",
  location_type: "Oakland", description: "", features: "", is_popular: false, is_active: true,
};

const CATEGORY_TABS = ["All", "Individual", "Class Pass", "Corporate"];

const PRIORITY_ORDER = [
  "Free Trial Session", "3-Class Pass", "5-Class Pass", "10-Class Pass",
  "Small Group Training", "Functional Fitness Monthly", "Physique Transformation", "Sports Specific Performance",
  "Nutrition Coaching", "Travel / In-Home Add-On", "Corporate & Tech Executive Wellness",
];

export default function PackagesPanel() {
  const [packages, setPackages] = useState([]);
  const [categoryTab, setCategoryTab] = useState("All");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/packages").then(({ data }) => setPackages(data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const sortedPackages = [...packages].sort((a, b) => {
    const ai = PRIORITY_ORDER.indexOf(a.name);
    const bi = PRIORITY_ORDER.indexOf(b.name);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
  const visiblePackages = categoryTab === "All" ? sortedPackages : sortedPackages.filter((p) => p.category === categoryTab);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, session_count: p.session_count ?? "", features: p.features.join("\n") });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      session_count: form.session_count ? parseInt(form.session_count) : null,
      features: form.features.split("\n").map((f) => f.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.put(`/admin/packages/${editing.id}`, payload);
      else await api.post("/admin/packages", payload);
      toast.success(editing ? "Package updated" : "Package created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save package");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    await api.put(`/admin/packages/${p.id}`, { ...p, is_active: !p.is_active });
    load();
  };

  const togglePopular = async (p) => {
    await api.put(`/admin/packages/${p.id}`, { ...p, is_popular: !p.is_popular });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/admin/packages/${id}`);
    toast.success("Package deleted");
    load();
  };

  return (
    <div data-testid="admin-packages-panel">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl text-white tracking-tight">Package & Pricing Manager</h1>
        <Button data-testid="create-package-button" onClick={openCreate} className="bg-[#D4AF37] text-black hover:bg-white rounded-full">
          <Plus size={16} /> New Package
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap mb-6">
        {CATEGORY_TABS.map((t) => (
          <button
            key={t}
            data-testid={`package-category-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setCategoryTab(t)}
            className={`px-4 py-1.5 rounded-full text-xs border transition-colors ${
              categoryTab === t ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#27272A] text-[#A1A1AA] hover:border-white/30"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visiblePackages.map((p) => (
          <div key={p.id} data-testid={`admin-package-card-${p.id}`} className="bg-[#141414] border border-[#27272A] rounded-2xl p-5">
            <div className="flex items-start justify-between mb-3">
              <p className="font-heading text-lg text-white tracking-tight">{p.name}</p>
              {p.is_popular && <span className="text-xs bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-1 rounded-full shrink-0">Popular</span>}
            </div>
            <div className="flex flex-wrap gap-1.5 mb-3">
              <span className="text-[10px] uppercase font-semibold bg-white/5 text-[#D4D4D8] px-2 py-1 rounded-full">{p.category}</span>
              <span className="text-[10px] uppercase font-semibold bg-white/5 text-[#D4D4D8] px-2 py-1 rounded-full">{p.billing_cycle}</span>
              <span className="text-[10px] uppercase font-semibold bg-white/5 text-[#D4D4D8] px-2 py-1 rounded-full">{p.location_type}</span>
            </div>
            <p className="font-heading text-2xl text-white mb-3">{p.price > 0 ? `$${p.price}` : "Free"}</p>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Switch data-testid={`package-active-switch-${p.id}`} checked={p.is_active} onCheckedChange={() => toggleActive(p)} />
                <span className="text-xs text-[#A1A1AA]">{p.is_active ? "Active" : "Inactive"}</span>
              </div>
              <div className="flex gap-3">
                <button data-testid={`edit-package-button-${p.id}`} onClick={() => openEdit(p)} className="text-[#A1A1AA] hover:text-white">
                  <PencilSimple size={18} />
                </button>
                <button data-testid={`delete-package-button-${p.id}`} onClick={() => remove(p.id)} className="text-[#A1A1AA] hover:text-red-400">
                  <Trash size={18} />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 border-t border-[#27272A] pt-3">
              <Switch data-testid={`package-popular-toggle-${p.id}`} checked={p.is_popular} onCheckedChange={() => togglePopular(p)} />
              <span className="text-xs text-[#A1A1AA]">Most Popular Badge</span>
            </div>
          </div>
        ))}
        {!visiblePackages.length && <p className="text-[#71717A] col-span-full text-center py-10">No packages in this category.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="package-form-dialog" className="bg-[#141414] border-[#27272A] text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">{editing ? "Edit Package" : "New Package"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <input
              data-testid="package-name-input"
              placeholder="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white"
            />
            <div className="grid grid-cols-2 gap-3">
              <select
                data-testid="package-category-select"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                className="bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white"
              >
                {PACKAGE_CATEGORIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
              <select
                data-testid="package-billing-select"
                value={form.billing_cycle}
                onChange={(e) => setForm((f) => ({ ...f, billing_cycle: e.target.value }))}
                className="bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white"
              >
                {BILLING_CYCLES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                data-testid="package-price-input"
                type="number"
                placeholder="Price ($)"
                value={form.price}
                onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                className="bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white"
              />
              <input
                data-testid="package-session-count-input"
                type="number"
                placeholder="Session count"
                value={form.session_count}
                onChange={(e) => setForm((f) => ({ ...f, session_count: e.target.value }))}
                className="bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white"
              />
            </div>
            <select
              data-testid="package-location-select"
              value={form.location_type}
              onChange={(e) => setForm((f) => ({ ...f, location_type: e.target.value }))}
              className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white"
            >
              {LOCATION_TYPES.map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
            <textarea
              data-testid="package-description-input"
              placeholder="Description"
              rows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white"
            />
            <textarea
              data-testid="package-features-input"
              placeholder="Feature bullets, one per line"
              rows={4}
              value={form.features}
              onChange={(e) => setForm((f) => ({ ...f, features: e.target.value }))}
              className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white"
            />
            <div className="flex items-center gap-3">
              <Switch data-testid="package-popular-switch" checked={form.is_popular} onCheckedChange={(v) => setForm((f) => ({ ...f, is_popular: v }))} />
              <span className="text-sm text-[#A1A1AA]">Mark as Most Popular</span>
            </div>
            <Button data-testid="package-save-button" onClick={save} disabled={saving || !form.name} className="w-full bg-[#D4AF37] text-black hover:bg-white rounded-full">
              {saving ? "Saving..." : "Save Package"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
